/** Gateways page - consolidated view of all supplier gateways. */
import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Grid,
} from "@mui/material";
import {
  Refresh,
  Add,
  Edit,
  Delete,
} from "@mui/icons-material";
import { suppliersApi } from "../api/client";

interface Gateway {
  id: number;
  supplier_id: string;
  hostname: string;
  ip_address: string;
  port: number;
  auth_type: string;
  username?: string;
  enabled: boolean;
  load_share: number;
  priority: number;
  capacity_cps: number;
  capacity_cc: number;
  created_at: string;
  updated_at: string;
}

interface Supplier {
  id: string;
  name: string;
  hostname: string;
  status: string;
}

interface GatewayWithSupplier extends Gateway {
  supplier_name?: string;
  supplier_status?: string;
}

const statusColors: Record<string, "success" | "error" | "default"> = {
  active: "success",
  enabled: "success",
  disabled: "error",
};

const authTypeLabels: Record<string, string> = {
  ip: "IP Whitelist",
  userpass: "Username/Password",
};

/** Gateways page component. */
const Gateways: React.FC = () => {
  const [gateways, setGateways] = useState<GatewayWithSupplier[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState<GatewayWithSupplier | null>(null);
  const [formData, setFormData] = useState({
    supplier_id: "",
    hostname: "",
    ip_address: "",
    port: 5060,
    auth_type: "ip",
    username: "",
    password: "",
    enabled: true,
    load_share: 1,
    priority: 0,
    capacity_cps: 100,
    capacity_cc: 1000,
    strip_digits: 0,
    prepend_prefix: "",
  });

  // Fetch all gateways from all suppliers
  const fetchGateways = async () => {
    setLoading(true);
    setError("");
    try {
      // Fetch suppliers
      const suppliersResponse = await suppliersApi.list({ page: 1, size: 100 });
      setSuppliers(suppliersResponse.data.items);

      // Fetch gateways for each supplier
      const allGateways: GatewayWithSupplier[] = [];
      for (const supplier of suppliersResponse.data.items) {
        try {
          const gatewaysResponse = await suppliersApi.listGateways(supplier.id);
          const gatewaysWithSupplier = gatewaysResponse.data.map((gw: Gateway) => ({
            ...gw,
            supplier_name: supplier.name,
            supplier_status: supplier.status,
          }));
          allGateways.push(...gatewaysWithSupplier);
        } catch {
          // Supplier has no gateways yet — not an error, just skip it
        }
      }
      setGateways(allGateways);
    } catch (err: any) {
      console.error("Failed to fetch gateways:", err);
      setError(err.response?.data?.detail || "Failed to load gateways");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGateways();
  }, []);

  const handleAddClick = () => {
    setSelectedGateway(null);
    setFormData({
      supplier_id: "",
      hostname: "",
      ip_address: "",
      port: 5060,
      auth_type: "ip",
      username: "",
      password: "",
      enabled: true,
      load_share: 1,
      priority: 0,
      capacity_cps: 100,
      capacity_cc: 1000,
      strip_digits: 0,
      prepend_prefix: "",
    });
    setEditDialogOpen(true);
  };

  const handleEditClick = (gateway: GatewayWithSupplier) => {
    setSelectedGateway(gateway);
    setFormData({
      supplier_id: gateway.supplier_id,
      hostname: gateway.hostname,
      ip_address: gateway.ip_address,
      port: gateway.port,
      auth_type: gateway.auth_type,
      username: gateway.username || "",
      password: "",
      enabled: gateway.enabled,
      load_share: gateway.load_share,
      priority: gateway.priority,
      capacity_cps: gateway.capacity_cps,
      capacity_cc: gateway.capacity_cc,
      strip_digits: (gateway as any).strip_digits ?? 0,
      prepend_prefix: (gateway as any).prepend_prefix ?? "",
    });
    setEditDialogOpen(true);
  };

  const handleDeleteClick = async (gateway: GatewayWithSupplier) => {
    try {
      await suppliersApi.removeGateway(gateway.supplier_id, gateway.id);
      fetchGateways();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to delete gateway");
    }
  };

  const handleSave = async () => {
    try {
      if (selectedGateway) {
        // blank password/username on edit = keep stored credentials
        const { password, username, ...rest } = formData;
        const payload: typeof formData = { ...rest } as typeof formData;
        if (password) (payload as any).password = password;
        if (username) (payload as any).username = username;
        await suppliersApi.updateGateway(
          selectedGateway.supplier_id,
          selectedGateway.id,
          payload
        );
      } else {
        await suppliersApi.createGateway(formData.supplier_id, formData);
      }
      setEditDialogOpen(false);
      fetchGateways();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to save gateway");
    }
  };

  // Summary stats
  const totalGateways = gateways.length;
  const enabledGateways = gateways.filter((g) => g.enabled).length;
  const disabledGateways = totalGateways - enabledGateways;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Gateways
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddClick}
          >
            Add Gateway
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchGateways}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Gateways
              </Typography>
              <Typography variant="h4">{totalGateways}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Enabled
              </Typography>
              <Typography variant="h4" sx={{ color: "success.main" }}>
                {enabledGateways}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Disabled
              </Typography>
              <Typography variant="h4" sx={{ color: "error.main" }}>
                {disabledGateways}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Gateways Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : gateways.length === 0 ? (
            <Box textAlign="center" p={4}>
              <Typography variant="body1" color="text.secondary">
                No gateways configured. Add a gateway to get started.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddClick}
                sx={{ mt: 2 }}
              >
                Add First Gateway
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Supplier</TableCell>
                    <TableCell>Hostname</TableCell>
                    <TableCell>IP Address</TableCell>
                    <TableCell>Port</TableCell>
                    <TableCell>Auth Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Load Share</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Capacity</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {gateways.map((gateway) => (
                    <TableRow key={gateway.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{gateway.supplier_name}</Typography>
                          <Chip
                            size="small"
                            label={gateway.supplier_status}
                            color={statusColors[gateway.supplier_status || ""] || "default"}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>{gateway.hostname || gateway.ip_address}</TableCell>
                      <TableCell>{gateway.ip_address}</TableCell>
                      <TableCell>{gateway.port}</TableCell>
                      <TableCell>
                        {authTypeLabels[gateway.auth_type] || gateway.auth_type}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={gateway.enabled ? "Enabled" : "Disabled"}
                          color={gateway.enabled ? "success" : "error"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{gateway.load_share}</TableCell>
                      <TableCell>{gateway.priority}</TableCell>
                      <TableCell>
                        {gateway.capacity_cps} CPS / {gateway.capacity_cc} CC
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(gateway)}
                          aria-label="Edit gateway"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(gateway)}
                          color="error"
                          aria-label="Delete gateway"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedGateway ? "Edit Gateway" : "Add Gateway"}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Supplier</InputLabel>
            <Select
              value={formData.supplier_id}
              label="Supplier"
              onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
              disabled={!!selectedGateway}
            >
              {suppliers.map((supplier) => (
                <MenuItem key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Hostname (optional)"
            value={formData.hostname}
            onChange={(e) => setFormData({ ...formData, hostname: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="IP Address"
            value={formData.ip_address}
            onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            type="number"
            label="Port"
            value={formData.port}
            onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Auth Type</InputLabel>
            <Select
              value={formData.auth_type}
              label="Auth Type"
              onChange={(e) => setFormData({ ...formData, auth_type: e.target.value })}
            >
              <MenuItem value="ip">IP Whitelist</MenuItem>
              <MenuItem value="userpass">Username/Password</MenuItem>
            </Select>
          </FormControl>
          {formData.auth_type === "userpass" && (
            <>
              <TextField
                fullWidth
                label="Username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                margin="normal"
                helperText="Leave blank on edit to keep the stored username"
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                margin="normal"
                helperText="Leave blank on edit to keep the stored password"
              />
            </>
          )}
          <TextField
            fullWidth
            type="number"
            label="Strip digits"
            value={formData.strip_digits}
            onChange={(e) => setFormData({ ...formData, strip_digits: parseInt(e.target.value) || 0 })}
            margin="normal"
            helperText="Digits stripped from the front of the dialed number (0 = none)"
          />
          <TextField
            fullWidth
            label="Prepend prefix"
            value={formData.prepend_prefix}
            onChange={(e) => setFormData({ ...formData, prepend_prefix: e.target.value })}
            margin="normal"
            helperText="Prefix prepended to the dialed number (e.g. 00 or +); empty = none"
          />
          <TextField
            fullWidth
            label="Load Share"
            type="number"
            value={formData.load_share}
            onChange={(e) => setFormData({ ...formData, load_share: parseInt(e.target.value) })}
            margin="normal"
            helperText="Relative weight for load distribution"
          />
          <TextField
            fullWidth
            label="Priority"
            type="number"
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
            margin="normal"
            helperText="Lower = higher priority"
          />
          <TextField
            fullWidth
            label="Capacity (CPS)"
            type="number"
            value={formData.capacity_cps}
            onChange={(e) => setFormData({ ...formData, capacity_cps: parseInt(e.target.value) })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Capacity (CC)"
            type="number"
            value={formData.capacity_cc}
            onChange={(e) => setFormData({ ...formData, capacity_cc: parseInt(e.target.value) })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {selectedGateway ? "Update" : "Add"} Gateway
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Gateways;
